import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { Field, FieldArray, FormSection, reduxForm, getFormValues } from 'redux-form';
import { RadioField, TextField, SelectField } from './../../../components/fields';
import messages from '../../../text/messages';
import { number, maxValue, required } from '../../../utilities/validation';
import WizardButtons from '../../../components/wizard/navigator/buttons';
import { getCurrency, getCurrencySymbol } from '../../../utilities/string';
import { getAdminDetails } from '../../../reducers/users';

import './steps.css';
import selectOptions from '../../../text/selectOptions';
import { savePosition, getPosition } from '../../../reducers/positions';
import { setProgress } from '../../../reducers/wizard';

class PositionThePayStep extends React.Component {
    constructor(props) {
        super(props);

        this.getPayOptions = this.getPayOptions.bind(this);
        this.getHourlyPayFields = this.getHourlyPayFields.bind(this);
        this.getSoWPayFields = this.getSoWPayFields.bind(this);
        this.getPOBlock = this.getPOBlock.bind(this);
        this.getPOOptions = this.getPOOptions.bind(this);
        this.getRadioOptions = this.getRadioOptions.bind(this);
        this.renderProjects = this.renderProjects.bind(this);
        this.jobProject = this.jobProject.bind(this);
        this.submit = this.submit.bind(this);
    }

    componentDidMount() {
        const { userDetails, admin, getAdminDetails, getPosition, position, positionId } = this.props;
        if (!admin) {
            getAdminDetails(userDetails.admin_id);
        }

        if (!position.id) {
            getPosition(positionId);
        }
    }

    submit(values) {
        const { savePosition, setProgress, getPosition, next, pay_type, position } = this.props;

        const projects = values.projects.map(p => ({
            project_id: p.project_id,
            rate: pay_type === 'h' ? p.rate : undefined,
            project_value: pay_type === 's' ? p.project_value : undefined,
            rate_currency: p.rate_currency,
            hours_per_week: pay_type === 'h' ? p.hours_per_week : undefined
        }));

        setProgress({ id: position.id, entity: 'position' }, { ...(position.progress || {}), projects }, () => {
            savePosition({ ...values, rate_currency: values.projects[0].rate_currency }, () => {
                getPosition(position.id, true, res => {
                    next(3, res.id, 'default');
                });
            });
        });
    }

    getPayOptions(active) {
        return this.getRadioOptions('pay', active, [{ value: 'h', label: 'By the hour' }, { value: 's', label: 'By deliverable' }]);
    }

    getRadioOptions(id, active, choices) {
        return choices.map(choice => ({
            value: choice.value,
            label: (
                <div className="payment-type-option">
                    <i className="material-icons">{active === choice.value ? 'radio_button_checked' : 'radio_button_unchecked'}</i>
                    <FormattedMessage id={id + choice.value} defaultMessage={choice.label} tagName={'span'} />
                </div>
            )
        }));
    }

    getPOOptions(active) {
        return this.getRadioOptions('po', active, selectOptions.purchaseOrderStatus);
    }

    getHourlyPayFields(project, index) {
        const { currenciesOptions, currency, formValues, change } = this.props;
        return (
            <div className="row">
                <div className="col-sm-4 col-xs-12">
                    <Field
                        name="rate_currency"
                        validate={required}
                        component={SelectField}
                        options={currenciesOptions}
                        placeholder={messages.currency}
                        disabled={!!index}
                        onChange={(_, newValue) => {
                            if (!index && formValues.projects.length > 1) {
                                formValues.projects.forEach((p, i) => {
                                    if (i) {
                                        change(`projects[${i}].rate_currency`, newValue);
                                    }
                                });
                            }
                        }}
                    />
                </div>
                <div className="col-sm-4 col-xs-12">
                    <Field
                        name="rate"
                        validate={[number, required]}
                        component={TextField}
                        extras={{ left: getCurrencySymbol(currency), right: <FormattedMessage {...messages.perHour} /> }}
                        placeholder={messages.payRate}
                        type="number"
                    />
                </div>
                <div className="col-sm-4 col-xs-12">
                    <Field
                        name="hours_per_week"
                        validate={[number, maxValue(99)]}
                        component={TextField}
                        placeholder={messages.hoursPerWeekOptional}
                        type="number"
                    />
                </div>
            </div>
        );
    }

    getSoWPayFields(project, index) {
        const { currenciesOptions, currency } = this.props;
        return (
            <div className="row">
                <div className="col-sm-2 col-xs-12">
                    <Field
                        name="currency"
                        validate={required}
                        component={SelectField}
                        options={currenciesOptions}
                        placeholder={messages.currency}
                        disabled={!!index}
                    />
                </div>
                <div className="col-sm-4 col-xs-12">
                    <Field
                        name="project_value"
                        validate={[number, required]}
                        component={TextField}
                        extras={{ left: getCurrencySymbol(currency) }}
                        placeholder={messages.totalValue}
                        type="number"
                    />
                </div>
            </div>
        );
    }

    jobProject(selectedProjects, project, index) {
        const { pay_type, projectsOptions } = this.props;
        const selectedProjectsIds = selectedProjects.getAll().map(p => p.project_id);
        const currentProject = selectedProjects.get(index);
        const options = projectsOptions.filter(p => p.value === currentProject.project_id || !selectedProjectsIds.includes(p.value));

        return (
            <FormSection name={project} key={`${currentProject ? currentProject.id : 'none'}-${index}`}>
                <div className="row">
                    <div className="col-xs-6 col-sm-3">
                        <Field name="project_id" validate={required} component={SelectField} options={options} placeholder={messages.project} />
                    </div>
                    <div className="col-xs-6 col-sm-8">
                        {pay_type !== 's' ? this.getHourlyPayFields(currentProject, index) : this.getSoWPayFields(currentProject, index)}
                    </div>
                    {selectedProjects.length > 1 && (
                        <div className="col-xs-1">
                            <div className="text-primary clickable topOffset15" onClick={() => selectedProjects.remove(index)}>
                                <FormattedMessage {...messages.remove} />
                            </div>
                        </div>
                    )}
                </div>
            </FormSection>
        );
    }

    renderProjects({ fields }) {
        const { projectsOptions } = this.props;
        const showAdd = fields.length !== projectsOptions.length;

        return (
            <React.Fragment>
                <div className="topOffset15 group">
                    {fields.map((project, index) => this.jobProject(fields, project, index))}
                    {showAdd && (
                        <div className="row">
                            <div className="col-xs-12">
                                <div
                                    className="text-primary clickable fit"
                                    onClick={() => fields.push({ ...fields.get(fields.length - 1), project_id: undefined, hours_per_week: undefined })}
                                >
                                    <FormattedMessage {...messages.addProject} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </React.Fragment>
        );
    }

    getPOBlock() {
        const { currency, po_when } = this.props;
        const options = this.getPOOptions(po_when);
        const enabled = po_when === 'now';

        return (
            <div className="poSection">
                <div className="row">
                    <div className="col-xs-12">
                        <div className="payment-type-choice">
                            <Field name="po_when" component={RadioField} options={options} placeholder={messages.poWhen} />
                        </div>
                    </div>
                </div>
                {enabled && (
                    <div className="row">
                        <div className="col-sm-6 col-xs-12">
                            <Field name="po_number" validate={required} component={TextField} placeholder={messages.poNumber} />
                        </div>
                        <div className="col-sm-6 col-xs-12">
                            <Field
                                name="po_value"
                                validate={[number, required]}
                                component={TextField}
                                extras={{ left: getCurrencySymbol(currency) }}
                                placeholder={messages.poValue}
                                type="number"
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { handleSubmit, back, cancel, submitting, pay_type, admin } = this.props;
        if (!admin) {
            return null;
        }

        const options = this.getPayOptions(pay_type);

        return (
            <div>
                <form onSubmit={handleSubmit(values => this.submit(values))}>
                    <div className="row">
                        <div className="col-xs-6">
                            <div className="payment-type-choice">
                                <Field name="pay_type" component={RadioField} options={options} placeholder={messages.paymentType} />
                            </div>
                        </div>
                    </div>

                    <FieldArray name="projects" component={this.renderProjects} />

                    {this.getPOBlock()}

                    <WizardButtons disabled={submitting} cancel={{ action: cancel }} back={{ action: back }} />
                </form>
            </div>
        );
    }
}

PositionThePayStep = reduxForm({
    form: 'position-wizard',
    destroyOnUnmount: false,
    enableReinitialize: true,
    forceUnregisterOnUnmount: true
})(PositionThePayStep);

const mapStateToProps = (state, props) => {
    const positionId = state.routing.location.pathname !== '/position/new' ? state.routing.location.pathname.replace('/position/', '') : undefined;
    const position = positionId !== undefined ? state.positions[positionId] : {};
    const projects = state.projects.list;
    const projectsOptions = (projects || []).map(p => ({ value: p.id, label: p.name }));
    const currenciesOptions = Object.entries(state.identity.details.currencies || {}).reduce((a, [key, value]) => {
        if (value === 'active') {
            a.push({ value: key, label: key });
        }
        return a;
    }, []);

    const formValues = getFormValues('position-wizard')(state) || {};
    const countryCurrency = getCurrency(formValues.country);
    const currency = formValues.projects && formValues.projects.length ? formValues.projects[0].rate_currency : countryCurrency;

    return {
        pay_type: formValues.pay_type,
        po_when: formValues.po_when,
        projects,
        projectsOptions,
        admin: state.users.adminDetails,
        userDetails: state.identity.details,
        position,
        currenciesOptions,
        currency,
        formValues,
        initialValues: {
            ...props.initialValues,
            ...position,
            projects: (position.progress || {}).projects || [
                {
                    rate_currency: countryCurrency,
                    project_id: projectsOptions.length === 1 ? projectsOptions[0].value : undefined
                }
            ]
        }
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getAdminDetails,
            savePosition,
            getPosition,
            setProgress
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(PositionThePayStep);



// WEBPACK FOOTER //
// ./src/pages/position/steps/positionThePay.jsx